import React, { useState, useRef } from 'react';

const API = 'https://calendar.nareea.co.uk';

function formatBrief(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

function LoadingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: '40px 20px', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 10, height: 10, borderRadius: '50%', background: '#1a73e8',
            animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>
      <div style={{ fontSize: 14, color: '#5f6368', marginTop: 12 }}>Claude is preparing your Morning Brief...</div>
      <div style={{ fontSize: 12, color: '#9aa0a6' }}>Analyzing calendar, conflicts, timezones and travel</div>
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
          40% { transform: scale(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const PERIODS = [
  { id: 'today',  label: 'Today' },
  { id: '1week',  label: '1 Week' },
  { id: '2weeks', label: '2 Weeks' },
  { id: '3weeks', label: '3 Weeks' },
  { id: 'month',  label: '1 Month' },
];

const MorningBrief = () => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('today');
  const printRef = useRef(null);

  const fetchBrief = async (p) => {
    const selectedPeriod = p || period;
    setLoading(true);
    setError('');
    setBrief(null);
    try {
      const res = await fetch(`${API}/api/morning-brief?period=${selectedPeriod}`);
      const data = await res.json();
      if (data.error) setError(data.error);
      else setBrief(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (p) => {
    setPeriod(p);
  };

  const handleDownloadTxt = () => {
    if (!brief) return;
    const blob = new Blob([brief.brief], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `morning-brief-${brief.period}-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadHtml = () => {
    if (!brief) return;
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Morning Brief — ${brief.period} — ${new Date().toLocaleDateString()}</title>
  <style>
    body { font-family: 'Gill Sans', Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #333; line-height: 1.7; }
    h1 { color: #1a73e8; border-bottom: 2px solid #1a73e8; padding-bottom: 10px; }
    .meta { color: #666; font-size: 13px; margin-bottom: 30px; }
    .stats { display: flex; gap: 16px; margin: 20px 0; }
    .stat { background: #e8f0fe; padding: 8px 16px; border-radius: 8px; font-size: 13px; }
    strong { color: #1a1a3e; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <h1>Morning Brief</h1>
  <div class="meta">Luke Harries · Head of Growth · ElevenLabs · Generated ${new Date().toLocaleString()}</div>
  <div class="stats">
    <div class="stat"><strong>${brief.today?.length || 0}</strong> events today</div>
    <div class="stat"><strong>${brief.conflicts}</strong> conflicts</div>
    <div class="stat"><strong>${brief.total_events}</strong> total events</div>
  </div>
  <div>${formatBrief(brief.brief)}</div>
</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `morning-brief-${brief.period}-${new Date().toISOString().slice(0,10)}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printContent = `
      <html><head><title>Morning Brief</title>
      <style>
        body { font-family: 'Gill Sans', Arial, sans-serif; max-width: 700px; margin: 30px auto; color: #333; line-height: 1.7; font-size: 14px; }
        h1 { color: #1a1a3e; border-bottom: 2px solid #1a1a3e; padding-bottom: 8px; }
        .meta { color: #666; font-size: 12px; margin-bottom: 20px; }
        strong { color: #1a1a3e; }
      </style></head>
      <body>
        <h1>Morning Brief — ${PERIODS.find(p => p.id === brief.period)?.label}</h1>
        <div class="meta">Luke Harries · Head of Growth · ElevenLabs · ${new Date().toLocaleString()}</div>
        <div>${formatBrief(brief.brief)}</div>
      </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(printContent);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  return (
    <div style={{ maxWidth: 760, margin: '20px auto', padding: '0 16px', fontFamily: 'Google Sans, Arial, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 500, color: '#1a73e8' }}>Morning Brief</h3>
          <p style={{ margin: 0, fontSize: 13, color: '#5f6368' }}>AI-generated daily summary for Luke Harries</p>
        </div>

        {/* Period selector */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => handlePeriodChange(p.id)}
              style={{
                padding: '7px 16px', border: 'none', borderRadius: 20, cursor: 'pointer', fontSize: 13,
                background: period === p.id ? '#1a73e8' : '#f1f3f4',
                color: period === p.id ? '#fff' : '#5f6368',
                fontWeight: period === p.id ? 600 : 400,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Generate button */}
        <button
          onClick={() => fetchBrief(period)}
          disabled={loading}
          style={{ padding: '10px 24px', background: loading ? '#dadce0' : '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Generating...' : brief ? 'Regenerate' : 'Generate Brief'}
        </button>

        {/* Stats */}
        {brief && (
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ background: '#1a73e8', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600 }}>
            {brief.today?.length || 0} today
          </div>
          <div style={{ background: brief.conflicts > 0 ? '#ea4335' : '#34a853', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600 }}>
            {brief.conflicts} conflicts
          </div>
          <div style={{ background: '#5f6368', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600 }}>
            {brief.total_events} total events
          </div>            
            {brief.travel?.length > 0 && (
              <div style={{ background: '#fef7e0', borderRadius: 8, padding: '6px 12px', fontSize: 12 }}>
                Travel: <strong>{brief.travel[0]?.title?.substring(0, 25)}</strong>
              </div>
            )}
            <div style={{ fontSize: 11, color: '#9aa0a6', marginLeft: 'auto' }}>
              {new Date(brief.generated_at).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <LoadingDots />
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: '#fce8e6', borderRadius: 12, padding: 16, color: '#c5221f', fontSize: 14 }}>
          Error: {error}
        </div>
      )}

      {/* Brief content */}
      {brief && !loading && (
        <>
          <div ref={printRef} style={{ background: '#fff', borderRadius: 12, padding: 28, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 12 }}>
            <div
              style={{ fontSize: 14, lineHeight: 1.8, color: '#3c4043' }}
              dangerouslySetInnerHTML={{ __html: formatBrief(brief.brief) }}
            />
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={handleDownloadHtml}
              style={{ flex: 1, padding: '12px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 140 }}
            >
              Save to Laptop
            </button>
            <button
              onClick={handleDownloadTxt}
              style={{ flex: 1, padding: '12px', background: '#f1f3f4', color: '#3c4043', border: '1px solid #dadce0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 140 }}
            >
              Save as TXT
            </button>
            <button
              onClick={handlePrint}
              style={{ flex: 1, padding: '12px', background: '#34a853', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 140 }}
            >
              Print
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: '#9aa0a6', textAlign: 'center' }}>
            Copy saved to server: uploads/briefs/{brief.period}/
          </div>
        </>
      )}

      {/* Empty state */}
      {!brief && !loading && !error && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', color: '#5f6368' }}>
          <div style={{ fontSize: 15, marginBottom: 8 }}>Select period and click Generate Brief</div>
          <div style={{ fontSize: 13 }}>AI will analyze calendar, conflicts, timezones and travel</div>
        </div>
      )}
    </div>
  );
};

export default MorningBrief;

